import React, { FC, useRef } from 'react'

import {
  DefaultFeatureFlags,
  usePRFile,
  useStatus,
} from '@sketch-hq/sketch-web-renderer'
import { ErrorMessage, LoadingPlaceholder } from '@sketch/components'
import { IS_EMBEDDED } from '@sketch/constants'
import { ReactComponent as DocumentProcessing } from '@sketch/icons/document-processing-64'
import { WebRendererCanvas } from 'components/WebRendererCanvas'
import { useCanvasPresentationFile } from 'components/WebRendererCanvas/hooks'
import { useTheme } from 'styled-components'

import { ArtboardAnnotationOverlay } from 'modules/annotations/components'
import { DraftCommentProvider } from 'modules/annotations/components/DraftCommentContext'
import LoadingState from 'modules/shares/components/LoadingState'
import { PageCanvasError } from 'modules/shares/PageCanvasView/components/PageCanvasError'

import { ArtboardDetailInspectorOverlay } from '../ArtboardDetailInspectorOverlay'
import { ArtboardOriginContent } from '../ArtboardOriginContent'
import { useCanvasAnnotations } from 'modules/annotations/hooks'
import {
  InspectorLayersTreeNode,
  useInspectorContext,
  useInspectorHoveredNode,
} from '../../hooks'

import * as S from './ArtboardDetailCanvas.styles'
import {
  ArtboardDetailInspectorContextMenu,
  useInspectorContextMenu,
} from '../ArtboardDetailInspectorContextMenu'
import { isArtboard } from '../../hooks/inspector/sketchSceneLib/inspectorTreeNodeUtilsDeprecated'

interface ArtboardDetailCanvasProps {
  pageUUID: string
  artboardUUID: string
}

const ARTBOARD_DETAIL_CANVAS_FEATURE_FLAGS: Partial<
  typeof DefaultFeatureFlags
> = {
  canvasLayersEvents: true,
}

export const ArtboardDetailCanvas: FC<ArtboardDetailCanvasProps> = ({
  pageUUID,
  artboardUUID,
}) => {
  const canvasContainerRef = useRef<HTMLDivElement>(null)

  const theme = useTheme()
  const {
    isInspectorActive,
    inspectorDataIsReady,
    hasInspectorDataError,
    clickHandler,
    mouseMoveHandler,
  } = useInspectorContext()

  const isWaitingForInspectorData = isInspectorActive && !inspectorDataIsReady

  const {
    share,
    page,
    versionShortId,
    isRendering,
    hasError,
    isLoading,
  } = useCanvasPresentationFile({
    pageUUID,
  })
  const canvasStatus = useStatus()

  const prFile = usePRFile()
  const bounds = prFile?.getRootNode()?.getAbsoluteExtent() ?? {
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  }

  const {
    isPlacingDraftAnnotation,
    onMouseDown,
    onMouseUp,
    onTouchEnd,
    onTouchStart,
  } = useCanvasAnnotations({
    shareIdentifier: share.identifier,
    permanentPageId: pageUUID,
    permanentArtboardId: artboardUUID,
  })

  const {
    handleContextMenuEvent,
    contextMenuPosition,
    handleCloseContextMenu,
  } = useInspectorContextMenu(canvasContainerRef)
  const hoveredNode = useInspectorHoveredNode()

  if (isLoading) {
    return <LoadingState />
  }

  if (isRendering) {
    return (
      <ErrorMessage
        title="Rendering Canvas…"
        description="The canvas is rendering. When it’s ready, it will appear here."
        icon={<DocumentProcessing />}
      />
    )
  }

  if (hasError || !page || (isInspectorActive && hasInspectorDataError)) {
    return (
      <PageCanvasError
        description={
          <>
            There was a problem fetching the data. You can find more information
            on our <a href="https://status.sketch.com/">Status Page</a>.
          </>
        }
        showReloadCanvasAction
      />
    )
  }

  //
  // Only show it when the canvas is read but not the data
  const showInspectorLoadingDataOverlay =
    isInspectorActive &&
    canvasStatus.type === 'READY' &&
    isWaitingForInspectorData

  return (
    <S.Wrapper ref={canvasContainerRef}>
      <ArtboardDetailInspectorContextMenu
        contextMenuPosition={contextMenuPosition}
        onCloseMenu={handleCloseContextMenu}
      />
      <WebRendererCanvas
        isWaitingForMoreData={isWaitingForInspectorData}
        pageUUID={pageUUID}
        artboardUUID={artboardUUID}
        page={page}
        versionShortId={versionShortId}
        shareIdentifier={share.identifier}
        backgroundColor={theme.colors.background.secondary.B}
        featureFlags={ARTBOARD_DETAIL_CANVAS_FEATURE_FLAGS}
        webRenderCursor={getWebRenderCursor(
          isPlacingDraftAnnotation,
          hoveredNode
        )}
        webRenderContainerProps={{
          onContextMenu: handleContextMenuEvent,
          onClick: clickHandler,
          onMouseMove: mouseMoveHandler,
          onMouseDown,
          onMouseUp,
          onTouchEnd,
          onTouchStart,
        }}
      >
        {!IS_EMBEDDED && (
          <ArtboardOriginContent>
            <DraftCommentProvider>
              <ArtboardAnnotationOverlay
                shareIdentifier={share.identifier}
                permanentArtboardId={artboardUUID}
                artboardWidth={bounds.width}
                artboardHeight={bounds.height}
              />
            </DraftCommentProvider>
          </ArtboardOriginContent>
        )}
        {isInspectorActive && prFile && (
          <ArtboardDetailInspectorOverlay
            canvasContainerRef={canvasContainerRef}
          />
        )}
      </WebRendererCanvas>
      {/* We don't want this to child of WebRendererCanvas because isWaitingForMoreData prop
        will make the whole component to be blured but we want this overlay to be normal */}
      {showInspectorLoadingDataOverlay && (
        <S.LoadingOverlay datatest-id="loading-inspector">
          <LoadingPlaceholder dark={false} size="64px" />
          <span> Preparing Inspector… </span>
        </S.LoadingOverlay>
      )}
    </S.Wrapper>
  )
}

function getWebRenderCursor(
  isPlacingDraftAnnotation: boolean | undefined,
  hoveredNode: InspectorLayersTreeNode | null
) {
  if (isPlacingDraftAnnotation) {
    return 'copy'
  }

  if (hoveredNode && !isArtboard(hoveredNode)) {
    return 'auto'
  }

  return undefined
}
